import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft, Eye, EyeOff, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  ResponseSectionView,
  SectionStepper,
  addressKey,
  buildDefinitionView,
  encodeAnswer,
  isMalformed,
  isSectionVisible,
  rowWriteColumns,
  surveyTitle
} from '@/domains/survey';
import type {
  IAnswer,
  IAnswerAddress,
  IDefinitionSnapshot,
  IRenderState,
  IRepeatRow,
  IResponseState,
  ISnapshotUsage,
  UiValue
} from '@/domains/survey';
import { Lockup, ZoneProvider } from '@/design';
import { cyclesApi, type IRankingCycle } from '../api/cycles-api';
import { definitionsApi } from '../api/definitions-api';
import type { IDefinitionPreview, IPublishFinding, IVersionDocument } from '../api/definitions-contracts';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../components/admin-page';
import { versionPath } from '../paths';

/**
 * ADM-07 — preview and test mode: walk a survey the way an advisor will, before it opens.
 *
 * ## Why this screen writes nothing, and how you can tell
 *
 * AC2 says a preview creates no production response. That is not a promise made in a comment
 * here — it is the shape of the file. The only network call in it is
 * `definitionsApi.preview`, a `GET`; there is no response id, no revision, no write queue and
 * no submit. `useResponse` is deliberately NOT used, because everything that hook owns exists
 * to talk to a response that this screen does not have.
 *
 * Every answer typed here lives in React state and dies with the tab.
 *
 * ## What it does prove, and what it cannot
 *
 * The form is drawn by `ResponseSectionView` — the respondent's own renderer, not a copy — so
 * what appears here is what an advisor sees: labels resolved from the bank, field types,
 * option lists at their pinned version, grid and multi-year columns, constraints and their
 * input masks. That is the half of a survey a reader can check by looking at it.
 *
 * It cannot exercise **conditional logic**, and that is a property of the system rather than
 * a gap in this screen. `use-response.ts` states the division of labour plainly: *the server
 * owns the truth* — it evaluates the rules and returns the render document on every write.
 * There is no TypeScript mirror of the engine in this app (the snapshot even ships `rules` so
 * a planned one would have its input), so no amount of front-end work evaluates a rule
 * without a response on the server to evaluate it against. A preview that quietly folded
 * rules with a second, home-made evaluator would be worse than one that does not: it would
 * agree with the engine right up until the day it mattered.
 *
 * It cannot exercise **regulatory pre-population** either, for a plainer reason — nothing
 * writes `response_fact` anywhere in the product yet, so there is no prefill to show. The
 * server says which gates that leaves shut, per definition, in
 * `assumptions.unresolvedFactKeys`, and this screen prints the list rather than hiding it.
 *
 * Both limits are stated on the page, in the "What this preview does not prove" card. A clean
 * preview must not be mistaken for a tested survey, and the only way to stop that is to say
 * so where the walkthrough happens.
 *
 * ## The two visibility modes
 *
 * A usage carrying `visibilityDefault: 'hidden'` is invisible until a rule lifts it, and with
 * no engine here nothing ever will. Showing such questions unconditionally would misrepresent
 * what an advisor meets on opening the survey; hiding them forever would make the preview
 * unable to show the content it exists to check. So both are offered, and the toggle names
 * which is on:
 *
 * - **As it opens** (default) — baseline visibility, gated questions hidden, which is
 *   literally what a respondent sees before answering anything.
 * - **Everything** — gated questions revealed, so their labels, types and options can be
 *   read. Marked, so nobody reads it as "this is what they will see".
 */

/** Synthesized in place of the engine's document. Static, so it is defined once. */
const NO_FINDINGS: never[] = [];

/**
 * `clearedPrefill` — permanently empty here, and structurally so rather than by omission.
 *
 * The respondent runtime fills this set when a prefill proposal is **rejected**, so the field
 * can say "cleared — please enter your own answer" instead of silently going blank. Reaching
 * that state needs a proposal to reject, a proposal needs a `response_fact` row, and nothing
 * in the product writes one yet. It is the same reason `onConfirm` below is a no-op: not
 * merely unused, but unreachable.
 *
 * A shared constant rather than `new Set()` at the call site, which would hand the renderer a
 * fresh identity on every keystroke.
 */
const NO_ADDRESSES: ReadonlySet<string> = new Set<string>();

/**
 * Repeat groups that will show no rows here, and the reason each one will not.
 *
 * A repeat group materializes its rows three ways, and only one of them is the respondent's
 * doing. `count_driven` reads the answer to another usage; `data_driven` reads a fact key.
 * Both are the engine's work, on a stored response — so in a preview neither ever produces a
 * row, and an empty principals table reads as a broken definition when it is nothing of the
 * kind. `unbounded` with `user_can_add` is the one that works here, because adding a row is
 * a click rather than an evaluation.
 *
 * Worth naming rather than describing in the abstract: a reader checking the RIA survey will
 * meet exactly this in section 3 and needs to know which of the two it is.
 */
function unmaterializedRepeats(snapshot: IDefinitionSnapshot) {
  return snapshot.usages
    .filter((usage) => usage.fieldType === 'repeat_group')
    .flatMap((usage) => {
      const repeat = usage.params?.repeat;
      if (!repeat) return [];
      if (repeat.mode === 'count_driven') {
        return [
          {
            code: usage.code,
            label: usage.label,
            why: `rows follow the answer to ${repeat.count_from_usage ?? 'another question'}, which only the engine counts`
          }
        ];
      }
      if (repeat.mode === 'data_driven') {
        return [
          {
            code: usage.code,
            label: usage.label,
            why: `rows come from the fact ${repeat.fact_key ?? '(unnamed)'}, and nothing writes facts yet`
          }
        ];
      }
      return [];
    });
}

export function AdminVersionPreviewPage() {
  const { versionId } = useParams<{ versionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The cycle lives in the URL rather than in state so a preview can be linked to and
   * reloaded — "look at 2027 under this version" is exactly the thing one person sends
   * another. `''` means no cycle, which the server answers with its synthetic one.
   */
  const cycleCode = searchParams.get('cycle') ?? '';
  const [revealGated, setRevealGated] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);

  const { data, error } = useAdminData(
    async (signal) => {
      const version = await definitionsApi.version(versionId!, signal);
      // Sequential, not parallel: the cycle list is filtered by this version's survey code,
      // and the preview read refuses an unknown cycle with a 400 rather than falling back —
      // so both of the reads below want the document first.
      const [preview, cycles] = await Promise.all([
        definitionsApi.preview(versionId!, cycleCode || null, signal),
        cyclesApi.list(signal)
      ]);
      return { version, preview, cycles };
    },
    [versionId, cycleCode]
  );

  if (error) {
    return (
      <AdminPage title="Preview">
        <LoadError message={error} />
      </AdminPage>
    );
  }

  if (!data) {
    return (
      <AdminPage title="Preview">
        <TableSkeleton />
      </AdminPage>
    );
  }

  return (
    <PreviewView
      version={data.version}
      preview={data.preview}
      cycles={data.cycles}
      cycleCode={cycleCode}
      onCycleChange={(next) => {
        setStepIndex(0);
        setSearchParams(next ? { cycle: next } : {}, { replace: true });
      }}
      revealGated={revealGated}
      onRevealChange={setRevealGated}
      stepIndex={stepIndex}
      onStepChange={setStepIndex}
    />
  );
}

interface IPreviewViewProps {
  version: IVersionDocument;
  preview: IDefinitionPreview;
  cycles: IRankingCycle[];
  cycleCode: string;
  onCycleChange: (code: string) => void;
  revealGated: boolean;
  onRevealChange: (reveal: boolean) => void;
  stepIndex: number;
  onStepChange: (index: number) => void;
}

/**
 * Split from the page so the hooks below run against a snapshot that is known to exist.
 * The alternative — hooks in the page, guarded on `data` — would either break the rules of
 * hooks or fill every memo with null checks for a case the page has already handled.
 */
function PreviewView(props: IPreviewViewProps) {
  const { version, preview, cycles, cycleCode, revealGated, stepIndex } = props;
  const { snapshot, assumptions } = preview;

  /** Cycles that pin this version's survey — the only ones whose config means anything here. */
  const offerableCycles = React.useMemo(
    () => cycles.filter((cycle) => cycle.surveys.some((s) => s.surveyCode === version.surveyCode)),
    [cycles, version.surveyCode]
  );

  const header = (
    <PreviewHeader
      version={version}
      cycles={offerableCycles}
      cycleCode={cycleCode}
      onCycleChange={props.onCycleChange}
    />
  );

  // 200 with no snapshot is a real answer, not an error: a finding stopped the preview from
  // producing one. Rendering the findings is the whole of what can be said.
  if (!snapshot) {
    return (
      <AdminPage title={`Preview — ${surveyTitle(version.surveyCode)} v${version.versionNo}`}>
        {header}
        <Alert variant="destructive">
          <AlertTitle>This version cannot be rendered</AlertTitle>
          <AlertDescription>
            The server could not produce a snapshot for it. The findings below say why — every
            one of them would also stop a respondent from being served this definition.
          </AlertDescription>
        </Alert>
        <FindingsCard findings={preview.findings} />
      </AdminPage>
    );
  }

  return (
    <AdminPage title={`Preview — ${surveyTitle(version.surveyCode)} v${version.versionNo}`}>
      {header}

      <LimitsCard
        assumptions={assumptions}
        cycleAsked={cycleCode}
        unmaterializedRepeats={unmaterializedRepeats(snapshot)}
        revealGated={revealGated}
        onRevealChange={props.onRevealChange}
      />

      <FindingsCard findings={preview.findings} />

      <AdvisorPortalFrame>
        <PreviewForm
          snapshot={snapshot}
          revealGated={revealGated}
          stepIndex={stepIndex}
          onStepChange={props.onStepChange}
        />
      </AdvisorPortalFrame>
    </AdminPage>
  );
}

/**
 * The respondent's renderer, drawn as the Advisor Portal draws it (the two-portals design, §4.4):
 * a frame in the portal's zone — `data-zone="portal"` on the frame itself, the nearer declaration
 * winning over the Team Portal's on `<html>`, and the portal's zone for the components that read
 * it — under a slim bar with the portal's lockup, so an author sees the questions in the type,
 * the controls and the ground an advisor will. It is a picture of that portal, not a way into it:
 * the bar has no navigation, and nothing here crosses zones.
 */
function AdvisorPortalFrame({ children }: { children: React.ReactNode }) {
  return (
    <ZoneProvider zone="portal">
      <div
        data-zone="portal"
        className="overflow-hidden rounded-xl border border-border bg-[var(--color-off-white)] text-base text-foreground"
      >
        <div className="flex h-12 items-center justify-between border-b border-border bg-white px-5">
          <Lockup zoneName="Advisor Portal" />
          <span className="text-xs text-muted-foreground">How an advisor sees it</span>
        </div>
        <div className="mx-auto grid w-full max-w-[1120px] gap-6 p-6 lg:p-8">{children}</div>
      </div>
    </ZoneProvider>
  );
}

function PreviewHeader({
  version,
  cycles,
  cycleCode,
  onCycleChange
}: {
  version: IVersionDocument;
  cycles: IRankingCycle[];
  cycleCode: string;
  onCycleChange: (code: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-1">
            <CardTitle className="flex flex-wrap items-center gap-2">
              {surveyTitle(version.surveyCode)}
              <Badge variant="outline">v{version.versionNo}</Badge>
              <Badge variant={version.status === 'published' ? 'default' : 'secondary'}>
                {version.status}
              </Badge>
              <Badge variant="outline">{version.origin}</Badge>
            </CardTitle>
            <CardDescription>
              Walk this version the way an advisor will. Nothing typed here is sent anywhere.
            </CardDescription>
          </div>

          <Button asChild variant="outline">
            <Link to={versionPath(version.surveyVersionId)}>
              <ArrowLeft className="size-4" aria-hidden /> Back to the editor
            </Link>
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid gap-2 sm:max-w-md">
          <label className="text-sm font-medium" htmlFor="preview-cycle">
            Render against cycle
          </label>
          <Select value={cycleCode || 'none'} onValueChange={(v) => onCycleChange(v === 'none' ? '' : v)}>
            <SelectTrigger id="preview-cycle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No cycle — synthetic config</SelectItem>
              {cycles.map((cycle) => (
                <SelectItem key={cycle.code} value={cycle.code}>
                  {cycle.code} — {cycle.name} ({cycle.status})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            A cycle supplies <code>as_of_date</code> and <code>year_labels</code>, and resolves
            every <code>max_from</code> upper bound. Without one the server substitutes an empty
            config, so bounds resolve to no ceiling and multi-year columns lose their headings.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The honest card. It is deliberately not collapsible and deliberately above the form: its
 * whole job is to stop a clean-looking walkthrough being reported as a tested survey.
 */
function LimitsCard({
  assumptions,
  cycleAsked,
  unmaterializedRepeats: repeats,
  revealGated,
  onRevealChange
}: {
  assumptions: IDefinitionPreview['assumptions'];
  cycleAsked: string;
  unmaterializedRepeats: { code: string; label: string; why: string }[];
  revealGated: boolean;
  onRevealChange: (reveal: boolean) => void;
}) {
  const factKeys = assumptions.unresolvedFactKeys;

  return (
    <Card>
      <CardHeader>
        <CardTitle>What this preview does not prove</CardTitle>
        <CardDescription>
          Structure, labels, field types, option lists and input constraints are real — they are
          drawn by the respondent&apos;s own renderer. These are not.
        </CardDescription>
      </CardHeader>

      <CardContent className="grid gap-4">
        <ul className="grid gap-3 text-sm">
          <li>
            <span className="font-medium">Conditional logic is not evaluated.</span>{' '}
            <span className="text-muted-foreground">
              Rules are evaluated against a stored response, and every write returns its
              visibility and requiredness. This screen has no
              response by design, so no rule fires here. Questions appear at their authored
              defaults.
            </span>
          </li>
          <li>
            <span className="font-medium">Regulatory pre-population is not exercised.</span>{' '}
            <span className="text-muted-foreground">
              Nothing writes <code>response_fact</code> in the product yet, so there is no
              prefilled value to confirm or reject and no fact gate that can open.
            </span>
          </li>
          {factKeys.length > 0 && (
            <li>
              <span className="font-medium">
                {factKeys.length} fact {factKeys.length === 1 ? 'gate' : 'gates'} could not be
                resolved
              </span>{' '}
              <span className="text-muted-foreground">
                — branches behind these ship unfolded, here and for a respondent:
              </span>
              <div className="mt-2 flex flex-wrap gap-1">
                {factKeys.map((key) => (
                  <Badge key={key} variant="outline" className="font-mono text-xs">
                    {key}
                  </Badge>
                ))}
              </div>
            </li>
          )}
          {repeats.length > 0 && (
            <li>
              <span className="font-medium">
                {repeats.length} repeat {repeats.length === 1 ? 'group shows' : 'groups show'} no
                rows here.
              </span>{' '}
              <span className="text-muted-foreground">
                Materializing a row is the engine&apos;s work, so an empty table below is an
                artifact of previewing and not a fault in the definition:
              </span>
              <ul className="mt-2 grid gap-1">
                {repeats.map((repeat) => (
                  <li key={repeat.code} className="text-xs text-muted-foreground">
                    <code>{repeat.code}</code> — {repeat.label}: {repeat.why}
                  </li>
                ))}
              </ul>
            </li>
          )}
          {assumptions.upperBoundsUnresolved && (
            <li>
              <span className="font-medium">Upper bounds are unresolved.</span>{' '}
              <span className="text-muted-foreground">
                At least one <code>max_from</code> found no ceiling in the cycle config, so a
                number field that should be capped is not.
              </span>
            </li>
          )}
          {cycleAsked && assumptions.cycle === null && (
            <li>
              <span className="font-medium">No cycle config was resolved.</span>{' '}
              <span className="text-muted-foreground">
                The preview fell back to its synthetic cycle.
              </span>
            </li>
          )}
        </ul>

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant={revealGated ? 'default' : 'outline'}
            onClick={() => onRevealChange(!revealGated)}
          >
            {revealGated ? (
              <>
                <EyeOff className="size-4" aria-hidden /> Showing everything
              </>
            ) : (
              <>
                <Eye className="size-4" aria-hidden /> Showing it as it opens
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            {revealGated
              ? 'Rule-gated questions are revealed so their content can be read. An advisor does not see these until a rule lifts them.'
              : 'Questions that a rule must reveal are hidden, which is what an advisor meets on opening the survey.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function FindingsCard({ findings }: { findings: IPublishFinding[] }) {
  if (findings.length === 0) return null;

  const errors = findings.filter((f) => f.severity.toLowerCase() === 'error');

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {findings.length} {findings.length === 1 ? 'finding' : 'findings'}
        </CardTitle>
        <CardDescription>
          The same list the dry run and a refused publish carry. Errors block a publish;
          warnings do not.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-3">
          {[...errors, ...findings.filter((f) => !errors.includes(f))].map((finding, index) => (
            <li key={`${finding.code}-${finding.subject ?? index}`} className="grid gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={finding.severity.toLowerCase() === 'error' ? 'destructive' : 'secondary'}
                >
                  {finding.severity}
                </Badge>
                <code className="text-xs">{finding.code}</code>
                {finding.subject && (
                  <span className="text-xs text-muted-foreground">{finding.subject}</span>
                )}
              </div>
              <p className="text-sm">{finding.message}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * The walkthrough itself.
 *
 * Holds every answer in one map keyed by `addressKey`, the same triple the respondent runtime
 * addresses by, so the renderer's drafts, answers and `data-address` all line up without a
 * second addressing scheme to keep in step.
 */
function PreviewForm({
  snapshot,
  revealGated,
  stepIndex,
  onStepChange
}: {
  snapshot: IDefinitionSnapshot;
  revealGated: boolean;
  stepIndex: number;
  onStepChange: (index: number) => void;
}) {
  interface IEntry {
    address: IAnswerAddress;
    value: UiValue;
    usage: ISnapshotUsage;
  }

  const [entries, setEntries] = React.useState<Map<string, IEntry>>(new Map());
  const [malformed, setMalformed] = React.useState<ReadonlySet<string>>(new Set());
  const [repeats, setRepeats] = React.useState<IRepeatRow[]>([]);

  const view = React.useMemo(() => buildDefinitionView(snapshot), [snapshot]);

  /**
   * The engine's document, synthesized. Every default here is chosen so that an absent entry
   * means the same thing it means on the respondent path:
   *
   * - `usageVisibility` — `isUsageVisible` shows a usage whose key is absent, by documented
   *   intent, so the only entries written are the `false` ones for questions a rule would
   *   have to reveal. In "show everything" mode not even those are written.
   * - `required` — seeded from the usage's own `required_mode`. `by_rule` is left out: only
   *   the engine can decide it, and marking it required would be an invention.
   * - the rest are empty, which the helpers already read as "no override": all options
   *   visible, no variant active, labels as authored.
   */
  const render = React.useMemo<IRenderState>(() => {
    const usageVisibility: Record<string, boolean> = {};
    const required: Record<string, boolean> = {};

    for (const usage of snapshot.usages) {
      if (!revealGated && usage.visibilityDefault === 'hidden') usageVisibility[usage.code] = false;
      if (usage.required === 'required' || usage.required === 'required_when_visible') {
        required[usage.code] = true;
      }
    }

    return {
      usageVisibility,
      sectionVisibility: {},
      required,
      activeVariants: {},
      optionVisibility: {},
      textOverrides: {}
    };
  }, [snapshot, revealGated]);

  /** Encoded answers, for the renderer's read path and for anything that formats a value. */
  const answers = React.useMemo(() => {
    const indexed = new Map<string, IAnswer>();
    for (const [key, entry] of entries) {
      const columns = rowWriteColumns(entry.usage, entry.address.instanceKey);
      const payload = encodeAnswer(entry.value, entry.usage.fieldType, columns);
      if (payload === null) continue;
      indexed.set(key, {
        usage: entry.address.usage,
        instanceId: entry.address.instanceId,
        instanceKey: entry.address.instanceKey,
        value: payload,
        // Not a wire value: no answer here reaches a database. It reads as the origin a
        // respondent's own typing carries, which is what the renderer expects to see.
        origin: 'respondent',
        isExcluded: false,
        provenance: null
      });
    }
    return indexed;
  }, [entries]);

  /** Typed-but-not-yet-encoded values, so a control always shows exactly what was entered. */
  const drafts = React.useMemo(() => {
    const map = new Map<string, UiValue>();
    for (const [key, entry] of entries) map.set(key, entry.value);
    return map;
  }, [entries]);

  const state = React.useMemo<IResponseState>(
    () => ({
      // A constant, and it is never sent: no call in this file takes a response id. It exists
      // because the renderer's props are the respondent's shape, and inventing a uuid per
      // render would churn the memo below for nothing.
      responseId: 'preview',
      status: 'draft',
      revision: 0,
      surveyVersionId: snapshot.surveyVersionId,
      pathCode: snapshot.pathCode,
      cursor: null,
      answers: [...answers.values()],
      repeats,
      render,
      findings: NO_FINDINGS,
      acks: NO_FINDINGS,
      popups: NO_FINDINGS
    }),
    [snapshot, answers, repeats, render]
  );

  const sections = React.useMemo(
    () => view.sections.filter((section) => isSectionVisible(section, render)),
    [view, render]
  );

  const onChange = React.useCallback(
    (address: IAnswerAddress, value: UiValue, usage: ISnapshotUsage) => {
      const key = addressKey(address.usage, address.instanceId, address.instanceKey);
      const columns = rowWriteColumns(usage, address.instanceKey);

      setEntries((previous) => new Map(previous).set(key, { address, value, usage }));
      setMalformed((previous) => {
        const next = new Set(previous);
        // The same judgement the respondent runtime makes: `abc` in a count is held and
        // flagged rather than discarded, so the author sees the mask refuse it.
        if (isMalformed(value, usage.fieldType, columns)) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    []
  );

  const onAddRow = React.useCallback((usageCode: string) => {
    setRepeats((previous) => [
      ...previous,
      {
        usage: usageCode,
        // Unique within the tab, which is the whole lifetime of this state. `crypto` is
        // deliberately not reached for: this id never leaves the page.
        instanceId: `preview-${usageCode}-${previous.length + 1}`,
        ordinal: previous.filter((row) => row.usage === usageCode).length + 1,
        status: 'active',
        externalKey: null
      }
    ]);
  }, []);

  const onRemoveRow = React.useCallback((instanceId: string) => {
    setRepeats((previous) => previous.filter((row) => row.instanceId !== instanceId));
    setEntries((previous) => {
      const next = new Map(previous);
      for (const [key, entry] of previous) {
        if (entry.address.instanceId === instanceId) next.delete(key);
      }
      return next;
    });
  }, []);

  const reset = React.useCallback(() => {
    setEntries(new Map());
    setMalformed(new Set());
    setRepeats([]);
    onStepChange(0);
  }, [onStepChange]);

  if (sections.length === 0) {
    return (
      <Alert>
        <AlertTitle>Nothing to show</AlertTitle>
        <AlertDescription>
          This version has no section holding a visible question.
          {!revealGated && ' Every question in it may be rule-gated — try "Showing everything".'}
        </AlertDescription>
      </Alert>
    );
  }

  const step = sections[Math.min(stepIndex, sections.length - 1)];
  const isLast = stepIndex >= sections.length - 1;

  // The survey's own layout, less its status column: the sections down the left, the section's
  // questions beside them — the columns an advisor's screen is drawn in (`ResponseFormPage`).
  return (
    <div className="grid gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <nav aria-label="Sections" className="min-w-0">
        <p className="mb-3 ml-3 text-sm text-muted-foreground">
          {sections.length === 1 ? '1 section' : `${sections.length} sections`}
        </p>
        <SectionStepper
          sections={sections}
          currentIndex={Math.min(stepIndex, sections.length - 1)}
          findings={NO_FINDINGS}
          onSelect={onStepChange}
        />
      </nav>

      <div className="min-w-0">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-[1.75rem] leading-tight font-light text-[var(--color-royal-blue)]">
              {step.section.title}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Section {stepIndex + 1} of {sections.length}
              {entries.size > 0 && ` · ${entries.size} answered in this walkthrough`}
            </p>
          </div>
          {entries.size > 0 && (
            <Button type="button" variant="ghost" onClick={reset}>
              <RotateCcw className="size-4" aria-hidden /> Start over
            </Button>
          )}
        </div>

        {/* `noValidate`, and no submit handler: there is nothing to submit to. Wrapped in a
            form anyway because the controls below are form controls and a bare div changes
            how a browser groups them for assistive technology. */}
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <ResponseSectionView
            view={step}
            state={state}
            render={state.render}
            yearLabels={snapshot.cycle.config.year_labels}
            drafts={drafts}
            malformed={malformed}
            answers={answers}
            findings={new Map()}
            clearedPrefill={NO_ADDRESSES}
            readOnly={false}
            onChange={onChange}
            // Typed text is committed on blur in the respondent runtime because that is when
            // it is sent. Nothing is sent here, so there is nothing to do.
            onCommit={() => {}}
            // Prefill confirm / reject. No prefilled value can reach this screen — nothing
            // writes `response_fact` — so this is unreachable rather than merely unused.
            onConfirm={() => {}}
            onAddRow={onAddRow}
            onRemoveRow={onRemoveRow}
          />

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-border pt-6">
            {stepIndex > 0 && (
              <Button type="button" variant="outline" onClick={() => onStepChange(stepIndex - 1)}>
                Back
              </Button>
            )}
            {!isLast && (
              <Button type="button" onClick={() => onStepChange(stepIndex + 1)}>
                Next section
              </Button>
            )}
            {isLast && (
              <p className="text-sm text-muted-foreground">
                End of the survey. There is no submit here — a preview has nothing to submit to.
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
